import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";

// ============================================================================
// SOCKET CLIENT IMPLEMENTATION
// ============================================================================

class AdminSocketClient {
  constructor() {
    this.ws = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 999;
    this.reconnectDelay = 3000;
    this.messageHandlers = new Map();
    this.connectionPromise = null;
    this.heartbeatInterval = null;
    this.authToken = null;
    this.reconnectTimeout = null;
    this.isManuallyDisconnected = false;
    this.isInitialized = false;
    this.initializationPromise = null;

    // Rate limiting variables
    this.lastDashboardRequest = 0;
    this.lastServerStatusRequest = 0;

    // Import environment config
    import("../config/environment").then((module) => {
      const envConfig = module.default;
      this.currentEndpoint = envConfig.getWebSocketUrl();
      this.isDev = envConfig.isDevelopment;

      this.startGlobalSocketService();

      console.log(
        `AdminSocketClient initialized with ${
          this.isDev ? "development" : "production"
        } endpoint: ${this.currentEndpoint}`
      );
    });
  }

  startGlobalSocketService() {
    if (this.isInitialized) return;

    this.isInitialized = true;
    console.log("🔌 Starting Global Socket Service...");

    this.checkAndConnectWithToken();

    this.tokenCheckInterval = setInterval(() => {
      this.checkAndConnectWithToken();
    }, 5000);

    if (typeof window !== "undefined") {
      window.addEventListener("storage", (e) => {
        if (e.key === "token") {
          console.log("🔑 Token changed, reconnecting socket...");
          this.checkAndConnectWithToken();
        }
      });

      document.addEventListener("visibilitychange", () => {
        if (!document.hidden && !this.isConnected) {
          console.log("👁️ Page became visible, checking socket connection...");
          this.checkAndConnectWithToken();
        }
      });

      window.addEventListener("online", () => {
        console.log("🌐 Network came online, reconnecting socket...");
        this.checkAndConnectWithToken();
      });

      window.addEventListener("offline", () => {
        console.log("📡 Network went offline");
      });

      window.addEventListener("beforeunload", () => {
        console.log("🔄 Page navigating, socket will auto-reconnect...");
      });
    }
  }

  checkAndConnectWithToken() {
    const token = this.getStoredToken();

    if (token && !this.isConnected && !this.connectionPromise) {
      console.log("🔑 Token found, connecting socket...");
      this.connectWithToken(token);
    } else if (!token && this.isConnected) {
      console.log("❌ No token found, disconnecting socket...");
      this.disconnect();
    } else if (token && this.authToken !== token) {
      console.log("🔄 Token changed, reconnecting with new token...");
      this.disconnect();
      setTimeout(() => this.connectWithToken(token), 1000);
    }
  }

  getStoredToken() {
    if (typeof window !== "undefined") {
      return localStorage.getItem("token");
    }
    return null;
  }

  connectWithToken(token) {
    this.authToken = token;
    this.connect(token);
  }

  getCurrentEndpoint() {
    return this.currentEndpoint;
  }

  setDevelopmentMode(useDev) {
    this.isProd = !useDev;
    this.currentEndpoint = this.isProd
      ? this.endpoints.production
      : this.endpoints.development;
    console.log(
      `Environment set to ${this.isProd ? "production" : "development"}: ${
        this.currentEndpoint
      }`
    );

    if (this.isConnected) {
      this.disconnect();
      setTimeout(() => this.connect(this.authToken), 1000);
    }
  }

  getEnvironment() {
    return this.isProd ? "production" : "development";
  }

  switchEndpoint() {
    this.setDevelopmentMode(!this.isProd);
  }

  connect(token = null) {
    if (this.isConnected || this.connectionPromise) {
      return this.connectionPromise;
    }

    this.authToken = token || this.getStoredToken();
    this.isManuallyDisconnected = false;

    if (!this.authToken) {
      console.warn("No auth token available for socket connection");
      return Promise.reject(new Error("No auth token"));
    }

    this.connectionPromise = new Promise((resolve, reject) => {
      try {
        console.log(`🔌 Connecting to WebSocket: ${this.currentEndpoint}`);
        this.ws = new WebSocket(this.currentEndpoint);

        const connectionTimeout = setTimeout(() => {
          if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
            console.error("❌ WebSocket connection timeout");
            this.ws.close();
            this.connectionPromise = null;
            reject(new Error("Connection timeout"));
          }
        }, 15000);

        this.ws.onopen = (event) => {
          clearTimeout(connectionTimeout);
          console.log("✅ AdminSocket connected successfully");
          this.isConnected = true;
          this.reconnectAttempts = 0;
          this.connectionPromise = null;

          this.startHeartbeat();

          if (this.authToken) {
            this.authenticate(this.authToken);
          }

          this.subscribeToAdminUpdates();
          this.handleMessage("connection", { status: "connected" });

          if (typeof window !== "undefined") {
            window.dispatchEvent(
              new CustomEvent("socketConnected", {
                detail: { status: "connected", endpoint: this.currentEndpoint },
              })
            );
          }

          resolve(this.ws);
        };

        this.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            this.handleMessage(data.type, data);
          } catch (error) {
            console.error("Error parsing WebSocket message:", error);
          }
        };

        this.ws.onclose = (event) => {
          clearTimeout(connectionTimeout);
          console.log(
            `🔌 AdminSocket disconnected - Code: ${event.code}, Reason: ${event.reason}`
          );
          this.isConnected = false;
          this.connectionPromise = null;
          this.stopHeartbeat();

          this.handleMessage("disconnection", {
            code: event.code,
            reason: event.reason,
          });

          if (typeof window !== "undefined") {
            window.dispatchEvent(
              new CustomEvent("socketDisconnected", {
                detail: { code: event.code, reason: event.reason },
              })
            );
          }

          if (!this.isManuallyDisconnected) {
            this.attemptReconnect();
          }
        };

        this.ws.onerror = (error) => {
          clearTimeout(connectionTimeout);
          console.error("❌ AdminSocket error:", error);
          this.connectionPromise = null;

          if (this.ws.readyState === WebSocket.CONNECTING) {
            reject(error);
          }
        };
      } catch (error) {
        console.error("❌ Failed to create WebSocket connection:", error);
        this.connectionPromise = null;
        reject(error);
      }
    });

    return this.connectionPromise;
  }

  disconnect() {
    this.isManuallyDisconnected = true;

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      this.ws.close(1000, "Manual disconnect");
    }
    this.stopHeartbeat();
    this.isConnected = false;
    this.connectionPromise = null;
    this.reconnectAttempts = 0;
  }

  stopGlobalService() {
    this.isInitialized = false;
    this.isManuallyDisconnected = true;

    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
      this.tokenCheckInterval = null;
    }

    this.disconnect();
    console.log("🛑 Global Socket Service stopped");
  }

  authenticate(token) {
    if (!this.isConnected) {
      console.warn("Cannot authenticate - not connected");
      return;
    }

    this.send({
      type: "auth",
      token: token,
    });
  }

  send(data) {
    if (!this.isConnected || !this.ws) {
      console.warn("Cannot send message - not connected");
      return false;
    }

    try {
      this.ws.send(JSON.stringify(data));
      return true;
    } catch (error) {
      console.error("Error sending message:", error);
      return false;
    }
  }

  handleMessage(type, data) {
    const handlers = this.messageHandlers.get(type) || [];
    handlers.forEach((handler) => {
      try {
        handler(data);
      } catch (error) {
        console.error(`Error in message handler for ${type}:`, error);
      }
    });
  }

  on(messageType, handler) {
    if (!this.messageHandlers.has(messageType)) {
      this.messageHandlers.set(messageType, []);
    }
    this.messageHandlers.get(messageType).push(handler);
  }

  off(messageType, handler) {
    const handlers = this.messageHandlers.get(messageType);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  startHeartbeat() {
    this.heartbeatInterval = setInterval(() => {
      if (
        this.isConnected &&
        this.ws &&
        this.ws.readyState === WebSocket.OPEN
      ) {
        this.send({ type: "ping" });
      }
    }, 30000);
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  attemptReconnect() {
    if (this.isManuallyDisconnected) {
      console.log("⏸️ Skipping reconnection - manually disconnected");
      return;
    }

    const token = this.getStoredToken();
    if (!token) {
      console.log("❌ No token available, skipping reconnection");
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(
      this.reconnectDelay *
        Math.pow(1.5, Math.min(this.reconnectAttempts - 1, 10)),
      30000
    );

    console.log(
      `🔄 Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`
    );

    this.reconnectTimeout = setTimeout(() => {
      if (!this.isManuallyDisconnected && !this.isConnected) {
        this.connect(token).catch((error) => {
          console.error("❌ Reconnection failed:", error);
        });
      }
    }, delay);
  }

  subscribeToAdminUpdates() {
    let lastDashboardUpdate = 0;
    let lastServerUpdate = 0;
    let lastUsersUpdate = 0;
    const throttleDelay = 2000;

    this.on("dashboard_update", (data) => {
      const now = Date.now();
      if (now - lastDashboardUpdate > throttleDelay) {
        lastDashboardUpdate = now;
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("adminDashboardUpdate", { detail: data })
          );
        }
      }
    });

    this.on("server_status_update", (data) => {
      const now = Date.now();
      if (now - lastServerUpdate > throttleDelay) {
        lastServerUpdate = now;
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("serverStatusUpdate", { detail: data })
          );
        }
      }
    });

    this.on("online_users_update", (data) => {
      const now = Date.now();
      if (now - lastUsersUpdate > throttleDelay) {
        lastUsersUpdate = now;
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("onlineUsersUpdate", { detail: data })
          );
        }
      }
    });
  }

  requestDashboardData() {
    if (this.isConnected) {
      const now = Date.now();
      if (
        !this.lastDashboardRequest ||
        now - this.lastDashboardRequest > 10000
      ) {
        this.lastDashboardRequest = now;
        this.send({
          type: "admin_request_dashboard_data",
        });
      }
    }
  }

  requestServerStatus() {
    if (this.isConnected) {
      const now = Date.now();
      if (
        !this.lastServerStatusRequest ||
        now - this.lastServerStatusRequest > 10000
      ) {
        this.lastServerStatusRequest = now;
        this.send({
          type: "admin_request_server_status",
        });
      }
    }
  }

  getStatus() {
    return {
      isConnected: this.isConnected,
      environment: this.isProd ? "production" : "development",
      endpoint: this.currentEndpoint,
      reconnectAttempts: this.reconnectAttempts,
      isManuallyDisconnected: this.isManuallyDisconnected,
      hasToken: !!this.getStoredToken(),
      isInitialized: this.isInitialized,
    };
  }

  forceReconnect() {
    console.log("🔄 Forcing socket reconnection...");
    this.disconnect();
    setTimeout(() => {
      this.isManuallyDisconnected = false;
      this.checkAndConnectWithToken();
    }, 1000);
  }
}

// ============================================================================
// SINGLETON INSTANCE
// ============================================================================

const adminSocketClient = new AdminSocketClient();

// Global socket status for debugging
if (typeof window !== "undefined") {
  window.adminSocketClient = adminSocketClient;
  window.getSocketStatus = () => adminSocketClient.getStatus();
}

// ============================================================================
// REACT CONTEXT IMPLEMENTATION
// ============================================================================

const SocketContext = createContext();

export const SocketProvider = ({ children }) => {
  const [socketStatus, setSocketStatus] = useState({
    isConnected: false,
    isConnecting: false,
    error: null,
    reconnectAttempts: 0,
    lastConnected: null,
    lastDisconnected: null,
  });

  const updateSocketStatus = useCallback(() => {
    const status = adminSocketClient.getStatus();
    setSocketStatus((prev) => ({
      ...prev,
      isConnected: status.isConnected,
      reconnectAttempts: status.reconnectAttempts,
      hasToken: status.hasToken,
      environment: status.environment,
      endpoint: status.endpoint,
    }));
  }, []);

  const handleSocketConnected = useCallback((event) => {
    console.log("✅ Socket connected:", event.detail);
    setSocketStatus((prev) => ({
      ...prev,
      isConnected: true,
      isConnecting: false,
      error: null,
      lastConnected: new Date(),
    }));
  }, []);

  const handleSocketDisconnected = useCallback((event) => {
    console.log("❌ Socket disconnected:", event.detail);
    setSocketStatus((prev) => ({
      ...prev,
      isConnected: false,
      isConnecting: false,
      lastDisconnected: new Date(),
      error: event.detail.reason || "Connection lost",
    }));
  }, []);

  const handleStorageChange = useCallback(
    (e) => {
      if (e.key === "token") {
        console.log("🔑 Token changed in SocketProvider");
        updateSocketStatus();
      }
    },
    [updateSocketStatus]
  );

  useEffect(() => {
    console.log("🔌 SocketProvider: Initializing socket context...");

    updateSocketStatus();

    window.addEventListener("socketConnected", handleSocketConnected);
    window.addEventListener("socketDisconnected", handleSocketDisconnected);
    window.addEventListener("storage", handleStorageChange);

    const statusInterval = setInterval(updateSocketStatus, 5000);

    return () => {
      window.removeEventListener("socketConnected", handleSocketConnected);
      window.removeEventListener(
        "socketDisconnected",
        handleSocketDisconnected
      );
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(statusInterval);
    };
  }, [
    updateSocketStatus,
    handleSocketConnected,
    handleSocketDisconnected,
    handleStorageChange,
  ]);

  const socketMethods = useMemo(
    () => ({
      send: (data) => adminSocketClient.send(data),
      requestDashboardData: () => adminSocketClient.requestDashboardData(),
      requestServerStatus: () => adminSocketClient.requestServerStatus(),
      forceReconnect: () => adminSocketClient.forceReconnect(),
      subscribe: (eventType, handler) =>
        adminSocketClient.on(eventType, handler),
      unsubscribe: (eventType, handler) =>
        adminSocketClient.off(eventType, handler),
      getStatus: () => adminSocketClient.getStatus(),
      switchEnvironment: () => adminSocketClient.switchEndpoint(),
    }),
    []
  );

  const contextValue = useMemo(
    () => ({
      ...socketStatus,
      ...socketMethods,
    }),
    [socketStatus, socketMethods]
  );

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
};

// ============================================================================
// HOOKS AND UTILITIES
// ============================================================================

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocket must be used within a SocketProvider");
  }
  return context;
};

export const withSocket = (Component) => {
  return React.memo(function SocketWrappedComponent(props) {
    const socketContext = useSocket();
    return <Component {...props} socket={socketContext} />;
  });
};

// ============================================================================
// EXPORTS
// ============================================================================

export default adminSocketClient;
export { SocketContext };
